/**
 * MultiBadgeSelect — Type B : sélecteur multi-valeurs avec chips/badges
 *
 * Usage :
 *   <MultiBadgeSelect
 *     options={[{
 *       value: "id",
 *       label: "Nom",
 *       sublabel: "Info secondaire",
 *       image: "https://…/photo.jpg",   // vignette gauche (null → icône générique)
 *       badgeContent: <AvailabilityBadge/>,  // badge droit
 *       searchText: "texte de recherche custom",
 *     }]}
 *     value={selectedIds}
 *     onChange={setSelectedIds}
 *     placeholder="Ajouter..."
 *   />
 *
 * - Les éléments sélectionnés apparaissent sous forme de chips supprimables.
 * - Un champ de recherche permet de filtrer et d'ajouter des éléments.
 * - Sur mobile (< 768px) la liste s'ouvre en bottom sheet plein écran.
 * - Aucune troncature sur les suggestions (largeur libre, 2 lignes).
 */

import { useState, useRef, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { X, ChevronDown, Search, Bot } from "lucide-react";
import { cn } from "@/lib/utils";

export interface MultiBadgeOption {
  value: string;
  label: string;
  sublabel?: string;
  badgeContent?: ReactNode;
  /**
   * URL de vignette (32×32). Si définie mais null/vide → icône générique.
   * Si non définie (undefined) → pas de colonne image dans la ligne.
   */
  image?: string | null;
  /**
   * Texte utilisé pour la recherche à la place de `label`.
   * Utile pour combiner plusieurs champs (model + nickname + SN).
   */
  searchText?: string;
}

interface MultiBadgeSelectProps {
  options: MultiBadgeOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  className?: string;
  "data-testid"?: string;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

/** Vignette image 32×32 avec fallback icône générique — composant module-level (ne pas déplacer dans le corps d'un render). */
function OptionImage({ src }: { src: string | null | undefined }) {
  const [errored, setErrored] = useState(false);
  if (src === undefined) return null;
  if (!src || errored) {
    return (
      <div className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center shrink-0">
        <Bot className="w-4 h-4 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="w-8 h-8 rounded-md object-contain shrink-0 bg-secondary"
      onError={() => setErrored(true)}
    />
  );
}

export function MultiBadgeSelect({
  options,
  value,
  onChange,
  placeholder = "Ajouter...",
  emptyText = "Aucun résultat",
  className,
  "data-testid": testId,
}: MultiBadgeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const selected = options.filter((o) => value.includes(o.value));
  const remaining = options.filter((o) => {
    if (value.includes(o.value)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const haystack = (o.searchText ?? o.label).toLowerCase();
    const haystackSub = (o.sublabel ?? "").toLowerCase();
    return haystack.includes(q) || haystackSub.includes(q);
  });

  const addItem = useCallback(
    (val: string) => {
      if (!value.includes(val)) onChange([...value, val]);
      setSearch("");
      if (!isMobile) inputRef.current?.focus();
    },
    [value, onChange, isMobile]
  );

  const removeItem = useCallback(
    (val: string) => onChange(value.filter((v) => v !== val)),
    [value, onChange]
  );

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  useEffect(() => {
    if (!isMobile) return;
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open, isMobile]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open && !isMobile) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, isMobile]);

  /* ─── JSX de la liste de suggestions ─────────────────────────────────────── */
  const suggestionListJsx = (
    <div className="flex flex-col min-w-0">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={isMobile ? undefined : inputRef}
          autoFocus={isMobile}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ fontSize: "16px" }}
        />
      </div>
      <div className="max-h-56 overflow-y-auto">
        {remaining.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          remaining.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-secondary/60 transition-colors"
              onMouseDown={(e) => { e.preventDefault(); addItem(opt.value); }}
              data-testid={`option-${opt.value}`}
            >
              <OptionImage src={opt.image} />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold leading-tight">{opt.label}</span>
                {opt.sublabel && (
                  <span className="text-xs text-muted-foreground leading-tight mt-0.5">
                    {opt.sublabel}
                  </span>
                )}
              </div>
              {opt.badgeContent && <span className="shrink-0 ml-1">{opt.badgeContent}</span>}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={cn("relative", className)} data-testid={testId}>
      <div
        className={cn(
          "min-h-10 w-full rounded-md border bg-background px-3 py-2 cursor-text",
          "flex flex-wrap gap-1.5 items-center",
          open && "ring-2 ring-ring ring-offset-1"
        )}
        onClick={() => { setOpen(true); if (!isMobile) inputRef.current?.focus(); }}
      >
        {selected.map((opt) => (
          <span
            key={opt.value}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium border border-primary/20"
            data-testid={`chip-${opt.value}`}
          >
            {opt.label}
            <button
              type="button"
              className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5"
              onMouseDown={(e) => { e.stopPropagation(); removeItem(opt.value); }}
              data-testid={`remove-${opt.value}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {selected.length === 0 && !open && (
          <span className="text-sm text-muted-foreground">{placeholder}</span>
        )}
        {!open && (
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground shrink-0" />
        )}
      </div>

      {open && !isMobile && (
        <div className="absolute z-50 top-full left-0 mt-1 w-full min-w-[18rem] rounded-md border bg-popover shadow-lg overflow-hidden">
          {suggestionListJsx}
        </div>
      )}

      {open && isMobile && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={() => setOpen(false)}>
          <div
            className="bg-popover rounded-t-2xl border-t shadow-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-border rounded-full mx-auto mt-3 mb-1" />
            <div className="px-4 pb-3 pt-1">
              <p className="text-sm font-semibold text-center text-foreground">{placeholder}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {suggestionListJsx}
            </div>
            <div className="p-4 border-t">
              <button
                type="button"
                className="w-full py-2 rounded-lg bg-secondary text-sm font-medium"
                onClick={() => setOpen(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
